import React from 'react';
import { Box, Typography, Avatar, Skeleton } from '@mui/material';

/**
 * Vertical event timeline. Renders an audit log or activity stream as a list
 * of dots-on-a-line with actor + action + timestamp.
 *
 * events: [{
 *   id,
 *   actor:    string (display name)
 *   action:   string ("created", "approved", "uploaded commercial invoice")
 *   timestamp: ISO 8601 or pre-formatted string
 *   icon?:    React component for the dot
 *   color?:   token (success | error | warning | info | primary) — defaults primary
 *   meta?:    optional secondary text under the action
 * }]
 *
 * loading: boolean — renders 3 skeleton rows.
 * empty:   React node shown when events.length === 0 and !loading.
 */
const COLOR_MAP = {
  success: 'success.main', error: 'error.main', warning: 'warning.main',
  info: 'info.main',       primary: 'primary.main',
};

function Initials({ name }) {
  const initials = String(name || '?')
    .split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase()).join('') || '?';
  return (
    <Avatar sx={{ width: 24, height: 24, fontSize: 11, bgcolor: 'grey.300', color: 'text.primary' }}>
      {initials}
    </Avatar>
  );
}

export function Timeline({ events = [], loading = false, empty = null }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {[1, 2, 3].map(i => (
          <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
            <Skeleton variant="circular" width={24} height={24} />
            <Box sx={{ flex: 1 }}>
              <Skeleton width="60%" />
              <Skeleton width="40%" height={14} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }
  if (!events.length) return empty;

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Vertical connector line */}
      <Box sx={{
        position: 'absolute', left: 11, top: 12, bottom: 12,
        width: 1, bgcolor: 'divider',
      }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        {events.map((e, i) => {
          const Icon = e.icon;
          const dotColor = COLOR_MAP[e.color] || COLOR_MAP.primary;
          return (
            <Box key={e.id ?? i} sx={{ display: 'flex', gap: 1.5, position: 'relative' }}>
              {Icon ? (
                <Box sx={{
                  width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: 'background.paper', borderRadius: '50%', zIndex: 1,
                  color: dotColor, border: '1px solid', borderColor: dotColor,
                }}>
                  <Icon sx={{ fontSize: 14 }} />
                </Box>
              ) : (
                <Box sx={{
                  width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: 'background.paper', zIndex: 1,
                }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor }} />
                </Box>
              )}
              <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
                  {e.actor && (
                    <Typography variant="body2" component="span" fontWeight={600}>
                      {e.actor}
                    </Typography>
                  )}
                  <Typography variant="body2" component="span" color="text.secondary">
                    {e.action}
                  </Typography>
                </Box>
                {e.meta && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    {e.meta}
                  </Typography>
                )}
                {e.timestamp && (
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: 10, mt: 0.25 }}>
                    {e.timestamp}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export { Initials };
export default Timeline;
